import { tr, type Messages, type PartialMessages } from './locales/tr';
import { en } from './locales/en';
import { de } from './locales/de';
import { ar } from './locales/ar';
import {
  DEFAULT_LOCALE,
  FALLBACKS,
  isLocale,
  isRtl,
  LOCALE_NAMES,
  SUPPORTED_LOCALES,
  type Locale,
} from './meta';

export {
  DEFAULT_LOCALE,
  isLocale,
  isRtl,
  LOCALE_NAMES,
  RTL_LOCALES,
  SUPPORTED_LOCALES,
  type Locale,
} from './meta';

const CATALOGS: Record<Locale, PartialMessages> = { tr, en, de, ar };

/** `Accept-Language` başlığından desteklenen ilk dili seçer. */
export function negotiateLocale(
  header: string | undefined | null,
  fallback: Locale = DEFAULT_LOCALE,
): Locale {
  if (!header) return fallback;
  const parts = header
    .split(',')
    .map((p) => {
      const [tag, q] = p.trim().split(';q=');
      return { tag: (tag ?? '').toLowerCase().split('-')[0] ?? '', q: q ? Number(q) : 1 };
    })
    .sort((a, b) => b.q - a.q);
  return (parts.find((p) => isLocale(p.tag))?.tag as Locale | undefined) ?? fallback;
}

function lookup(catalog: PartialMessages, key: string): string | undefined {
  let cur: unknown = catalog;
  for (const part of splitKey(key)) {
    if (cur && typeof cur === 'object' && part in (cur as Record<string, unknown>))
      cur = (cur as Record<string, unknown>)[part];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

/** `permissions.org.user.read` gibi noktalı alt anahtarları destekler: ilk parça namespace, kalanı tek anahtar olabilir. */
function splitKey(key: string): string[] {
  const [ns, ...rest] = key.split('.');
  const restJoined = rest.join('.');
  const nsObj = (tr as Record<string, unknown>)[ns ?? ''];
  if (nsObj && typeof nsObj === 'object' && restJoined in (nsObj as Record<string, unknown>))
    return [ns!, restJoined];
  return key.split('.');
}

export function translate(locale: string, key: string, params: Record<string, string | number> = {}): string {
  const loc: Locale = isLocale(locale) ? locale : DEFAULT_LOCALE;
  for (const l of FALLBACKS[loc]) {
    const v = lookup(CATALOGS[l], key);
    if (v !== undefined)
      return v.replace(/\{\{(\w+)\}\}/g, (_, p: string) => String(params[p] ?? `{{${p}}}`));
  }
  return key;
}

export function createTranslator(locale: string) {
  return (key: string, params?: Record<string, string | number>) => translate(locale, key, params);
}

/** Bir dilde eksik olan anahtarları listeler (CI raporu ve testler için). */
export function missingKeys(locale: Locale): string[] {
  const out: string[] = [];
  const walk = (
    ref: Record<string, unknown>,
    target: Record<string, unknown> | undefined,
    prefix: string,
  ) => {
    for (const [k, v] of Object.entries(ref)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (typeof v === 'string') {
        if (!target || typeof target[k] !== 'string') out.push(path);
      } else walk(v as Record<string, unknown>, target?.[k] as Record<string, unknown> | undefined, path);
    }
  };
  walk(tr as unknown as Record<string, unknown>, CATALOGS[locale] as Record<string, unknown>, '');
  return out;
}

/** Dil başına arayüz çevirisi kapsamı (F7-17; panelde Çeviriler ekranı ve CI raporu). Referans: Türkçe. */
export function translationCoverage(sampleSize = 100): Array<{
  locale: Locale;
  name: string;
  rtl: boolean;
  total: number;
  missing: number;
  missingKeys: string[];
}> {
  const total = countStrings(tr as unknown as Record<string, unknown>);
  return SUPPORTED_LOCALES.map((locale) => {
    const keys = locale === 'tr' ? [] : missingKeys(locale);
    return {
      locale,
      name: LOCALE_NAMES[locale],
      rtl: isRtl(locale),
      total,
      missing: keys.length,
      missingKeys: keys.slice(0, sampleSize),
    };
  });
}

function countStrings(o: Record<string, unknown>): number {
  let n = 0;
  for (const v of Object.values(o))
    n += typeof v === 'string' ? 1 : countStrings(v as Record<string, unknown>);
  return n;
}

export function messagesFor(locale: Locale): PartialMessages {
  return CATALOGS[locale];
}

export type { Messages, PartialMessages };
export { tr };
